'use client'

import type { UseSearchBarDirectHitProductActionProps } from '@maestro/commerce-blocks/components/search-bar/search.types'

import { useMemo } from 'react'

import { useCartContext } from '@maestro/commerce-blocks/context/cart.context'
import { useMaestroSession } from '@maestro/commerce-blocks/providers/maestro-session/maestro-session.context'
import { theme } from '@maestro/local/theme'

export const useSearchBarDirectHitProductAction = (
  props: UseSearchBarDirectHitProductActionProps
) => {
  const { product } = props
  const { cart, getAvailableQuantity, updateCartEntry } = useCartContext()
  const { session } = useMaestroSession()
  const {
    isEnabledDirectHitQuantityPicker,
    showDirectHitActionOnlyForLoggedIn,
  } = theme().search.searchBar

  const variantId = product.defaultVariantId

  const isActionVisible = useMemo(() => {
    return !showDirectHitActionOnlyForLoggedIn || !!session
  }, [showDirectHitActionOnlyForLoggedIn, session])

  const cartEntry = useMemo(() => {
    if (!isEnabledDirectHitQuantityPicker) {
      return undefined
    }
    return cart.entries.find(
      (entry) => entry.productId === product.id && entry.variantId === variantId
    )
  }, [cart.entries, isEnabledDirectHitQuantityPicker, product.id, variantId])

  const maxQuantity = useMemo(() => {
    return getAvailableQuantity(product, variantId) + (cartEntry?.quantity ?? 0)
  }, [getAvailableQuantity, product, variantId, cartEntry?.quantity])

  const handleQuantityChange = (newQuantity: number) => {
    if (!cartEntry) {
      return
    }
    updateCartEntry(cartEntry.id, product.id, variantId, newQuantity)
  }

  return {
    isActionVisible,
    variantId,
    cartEntry,
    maxQuantity,
    handleQuantityChange,
  }
}
